/**
 * CallPeerConnection - WebRTC peer connection management using simple-peer
 * Handles individual peer-to-peer connections in the mesh network
 */

import SimplePeer from "simple-peer";
import type { PeerConnectionConfig } from "../types";

export interface PeerConnectionEvents {
  onSignal: (data: SimplePeer.SignalData) => void;
  onConnect: () => void;
  onData: (data: any) => void;
  onStream: (stream: MediaStream) => void;
  onTrack: (track: MediaStreamTrack, stream: MediaStream) => void;
  onClose: () => void;
  onError: (error: Error) => void;
}

export class CallPeerConnection {
  private peer: SimplePeer.Instance | null = null;
  private config: PeerConnectionConfig;
  private events: Partial<PeerConnectionEvents>;
  private connectionState: RTCPeerConnectionState = "new";
  private isInitiator: boolean;
  private remoteUserId: string;
  private localStream: MediaStream | null = null;

  constructor(
    remoteUserId: string,
    config: PeerConnectionConfig,
    events: Partial<PeerConnectionEvents> = {}
  ) {
    this.remoteUserId = remoteUserId;
    this.config = config;
    this.events = events;
    this.isInitiator = config.initiator || false;
  }

  /**
   * Create and initialize the peer connection
   */
  async createConnection(localStream?: MediaStream): Promise<void> {
    try {
      console.log(
        `[CallPeerConnection] Creating connection to ${this.remoteUserId}, initiator: ${this.isInitiator}`
      );

      this.localStream = localStream || null;

      const peerConfig: SimplePeer.Options = {
        initiator: this.isInitiator,
        trickle: true,
        stream: this.localStream || undefined,
        config: {
          iceServers: this.config.iceServers,
        },
      };

      this.peer = new SimplePeer(peerConfig);
      this.setupPeerEventListeners();

      console.log(
        `[CallPeerConnection] Peer connection created for ${this.remoteUserId}`
      );
    } catch (error) {
      console.error(
        `[CallPeerConnection] Failed to create connection to ${this.remoteUserId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Signal data to remote peer
   */
  signal(data: SimplePeer.SignalData): void {
    if (!this.peer) {
      console.error(
        `[CallPeerConnection] Cannot signal to ${this.remoteUserId}: peer not initialized`
      );
      return;
    }

    try {
      console.log(
        `[CallPeerConnection] Signaling to ${this.remoteUserId}:`,
        data.type
      );
      this.peer.signal(data);
    } catch (error) {
      console.error(
        `[CallPeerConnection] Failed to signal to ${this.remoteUserId}:`,
        error
      );
      this.events.onError?.(error as Error);
    }
  }

  /**
   * Add stream to the peer connection
   */
  addStream(stream: MediaStream): void {
    if (!this.peer) {
      console.error(
        `[CallPeerConnection] Cannot add stream to ${this.remoteUserId}: peer not initialized`
      );
      return;
    }

    try {
      // Remove existing stream if any
      if (this.localStream) {
        this.removeStream(this.localStream);
      }

      this.localStream = stream;
      this.peer.addStream(stream);

      console.log(`[CallPeerConnection] Stream added to ${this.remoteUserId}`);
    } catch (error) {
      console.error(
        `[CallPeerConnection] Failed to add stream to ${this.remoteUserId}:`,
        error
      );
      this.events.onError?.(error as Error);
    }
  }

  /**
   * Remove stream from the peer connection
   */
  removeStream(stream: MediaStream): void {
    if (!this.peer) {
      console.error(
        `[CallPeerConnection] Cannot remove stream from ${this.remoteUserId}: peer not initialized`
      );
      return;
    }

    try {
      this.peer.removeStream(stream);
      if (this.localStream === stream) {
        this.localStream = null;
      }

      console.log(
        `[CallPeerConnection] Stream removed from ${this.remoteUserId}`
      );
    } catch (error) {
      console.error(
        `[CallPeerConnection] Failed to remove stream from ${this.remoteUserId}:`,
        error
      );
      this.events.onError?.(error as Error);
    }
  }

  /**
   * Replace a track in the peer connection (useful for screen sharing)
   */
  replaceTrack(oldTrack: MediaStreamTrack, newTrack: MediaStreamTrack): void {
    if (!this.peer) {
      console.error(
        `[CallPeerConnection] Cannot replace track for ${this.remoteUserId}: peer not initialized`
      );
      return;
    }

    try {
      this.peer.replaceTrack(oldTrack, newTrack, this.localStream!);
      console.log(
        `[CallPeerConnection] Track replaced for ${this.remoteUserId}`
      );
    } catch (error) {
      console.error(
        `[CallPeerConnection] Failed to replace track for ${this.remoteUserId}:`,
        error
      );
      this.events.onError?.(error as Error);
    }
  }

  /**
   * Send data through the data channel
   */
  sendData(data: any): void {
    if (!this.peer) {
      console.error(
        `[CallPeerConnection] Cannot send data to ${this.remoteUserId}: peer not initialized`
      );
      return;
    }

    if (!this.peer.connected) {
      console.warn(
        `[CallPeerConnection] Cannot send data to ${this.remoteUserId}: not connected`
      );
      return;
    }

    try {
      this.peer.send(JSON.stringify(data));
    } catch (error) {
      console.error(
        `[CallPeerConnection] Failed to send data to ${this.remoteUserId}:`,
        error
      );
      this.events.onError?.(error as Error);
    }
  }

  /**
   * Set bandwidth limits
   */
  async setBandwidthLimit(maxBitrate: number): Promise<void> {
    if (!this.peer) {
      console.warn(
        `[CallPeerConnection] Cannot set bandwidth for ${this.remoteUserId}: peer connection not available`
      );
      return;
    }

    try {
      const pc = (this.peer as any)._pc as RTCPeerConnection;
      if (!pc) return;

      const senders = pc.getSenders();

      for (const sender of senders) {
        if (sender.track) {
          const parameters = sender.getParameters();

          if (!parameters.encodings) {
            parameters.encodings = [{}];
          }

          parameters.encodings[0].maxBitrate = maxBitrate * 1000; // Convert to bps
          await sender.setParameters(parameters);
        }
      }

      console.log(
        `[CallPeerConnection] Bandwidth limit set to ${maxBitrate}kbps for ${this.remoteUserId}`
      );
    } catch (error) {
      console.error(
        `[CallPeerConnection] Failed to set bandwidth limit for ${this.remoteUserId}:`,
        error
      );
    }
  }

  /**
   * Get connection state
   */
  getConnectionState(): RTCPeerConnectionState {
    return this.connectionState;
  }

  /**
   * Check if peer is connected
   */
  isConnected(): boolean {
    return this.peer?.connected === true;
  }

  /**
   * Get connection statistics
   */
  async getStats(): Promise<RTCStatsReport | null> {
    if (!this.peer) {
      return null;
    }

    try {
      const pc = (this.peer as any)._pc as RTCPeerConnection;
      if (!pc) return null;

      return await pc.getStats();
    } catch (error) {
      console.error(
        `[CallPeerConnection] Failed to get stats for ${this.remoteUserId}:`,
        error
      );
      return null;
    }
  }

  /**
   * Get remote user ID
   */
  getRemoteUserId(): string {
    return this.remoteUserId;
  }

  /**
   * Cleanup and destroy the peer connection
   */
  destroy(): void {
    console.log(
      `[CallPeerConnection] Destroying connection to ${this.remoteUserId}`
    );

    if (this.peer) {
      try {
        this.peer.destroy();
      } catch (error) {
        console.error(
          `[CallPeerConnection] Error destroying peer for ${this.remoteUserId}:`,
          error
        );
      }
      this.peer = null;
    }

    this.localStream = null;
    this.connectionState = "closed";
  }

  /**
   * Setup event listeners for the peer connection
   */
  private setupPeerEventListeners(): void {
    if (!this.peer) return;

    this.peer.on("signal", (data) => {
      console.log(
        `[CallPeerConnection] Signal from ${this.remoteUserId}:`,
        data.type
      );
      this.events.onSignal?.(data);
    });

    this.peer.on("connect", () => {
      console.log(`[CallPeerConnection] Connected to ${this.remoteUserId}`);
      this.connectionState = "connected";
      this.events.onConnect?.();
    });

    this.peer.on("data", (data) => {
      try {
        const parsedData = JSON.parse(data.toString());
        this.events.onData?.(parsedData);
      } catch (error) {
        console.error(
          `[CallPeerConnection] Failed to parse data from ${this.remoteUserId}:`,
          error
        );
      }
    });

    this.peer.on("stream", (stream) => {
      console.log(
        `[CallPeerConnection] Stream received from ${this.remoteUserId}`
      );
      this.events.onStream?.(stream);
    });

    this.peer.on("track", (track, stream) => {
      console.log(
        `[CallPeerConnection] Track received from ${this.remoteUserId}:`,
        track.kind
      );
      this.events.onTrack?.(track, stream);
    });

    this.peer.on("close", () => {
      console.log(
        `[CallPeerConnection] Connection closed with ${this.remoteUserId}`
      );
      this.connectionState = "closed";
      this.events.onClose?.();
    });

    this.peer.on("error", (error) => {
      console.error(
        `[CallPeerConnection] Error with ${this.remoteUserId}:`,
        error
      );
      this.connectionState = "failed";
      this.events.onError?.(error);
    });

    // Monitor connection state changes
    const pc = (this.peer as any)._pc as RTCPeerConnection;
    if (pc) {
      pc.addEventListener("connectionstatechange", () => {
        if (this.peer) {
          const pc = (this.peer as any)._pc as RTCPeerConnection;
          if (pc) {
            this.connectionState = pc.connectionState;
            console.log(
              `[CallPeerConnection] Connection state changed for ${this.remoteUserId}:`,
              this.connectionState
            );
          }
        }
      });
    }
  }

  /**
   * Get detailed connection info for debugging
   */
  getConnectionInfo() {
    return {
      remoteUserId: this.remoteUserId,
      isInitiator: this.isInitiator,
      connectionState: this.connectionState,
      connected: this.isConnected(),
      hasLocalStream: !!this.localStream,
      localStreamTracks: this.localStream
        ? {
            video: this.localStream.getVideoTracks().length,
            audio: this.localStream.getAudioTracks().length,
          }
        : null,
    };
  }
}
